import type { Manifest } from '@clawharbor/manifest';
import {
  clip,
  isProbeChatter,
  parseProbeLine,
  parseProposalLine,
  parseStageHeader,
  stripBullet,
} from './parse.js';
import { PHASES } from './types.js';
import type {
  Clearance,
  LoginResponse,
  Ownership,
  OwnershipChallenge,
  OwnershipStatus,
  OwnershipVerification,
  Phase,
  Principal,
  ProgressEvent,
  PublishFailure,
  StageState,
} from './types.js';

export type Screen = 'landing' | 'signin' | 'progress' | 'result';

export interface ConsoleState {
  screen: Screen;
  mode: 'live' | 'demo';

  /** Landing */
  input: string;
  inputError: string | null;
  starting: boolean;
  /**
   * What the person asserts about the site, sent with the crawl. Defaults to
   * `third-party` because that is the assertion that ADDS the disclaimer: a
   * wrong default the other way would print a stranger's brand as its owner's
   * own work on nothing but a pre-ticked box.
   */
  ownership: Ownership;

  /**
   * The address the person pressed the button on while signed out, held across
   * the sign-in step so the mailed link can carry it back. It is a normalized
   * URL rather than the raw field, because the field is what the person typed
   * and this is what would have been crawled.
   */
  pendingUrl: string | null;
  /**
   * An address recovered from `?url=` after a sign-in round trip, waiting for
   * ONE PRESS. It is never crawled on load: `next` is a value a stranger can
   * put in a mail, and a console that crawled whatever arrived in the query
   * string would spend the reader's budget on a site of the sender's choosing.
   */
  resumeUrl: string | null;

  /** Job */
  jobId: string | null;
  tenantId: string | null;
  targetUrl: string | null;
  startedAt: number | null;
  finishedAt: number | null;
  pct: number;
  stages: StageState[];
  /**
   * Every capability the synthesize stage declared, in the order it declared
   * them. The probe stage counts against this, so an operator can see what is
   * still being executed rather than watching a list appear from nowhere.
   */
  proposals: string[];
  clearances: Clearance[];
  fatal: string | null;

  /** Review */
  manifest: Manifest | null;
  acknowledged: boolean;

  /**
   * Who is asking, as `GET /api/auth/me` answered it. Asked once at boot for the
   * rail's label and again per result screen for the publish gate, which is why
   * `reset()` and `beginJob()` carry it across: a session does not end because
   * somebody pressed "New crawl", and a rail that flipped back to "Sign in"
   * there would be telling the reader they had been signed out.
   * `principalChecked` separates "nobody" from "not asked yet": the gate must
   * not offer a sign-in form for the half-second before the answer arrives and
   * then swap it for a publish button under the reader's cursor.
   */
  principal: Principal | null;
  principalChecked: boolean;

  /** Sign-in, from the gate. The address is held for the one request only. */
  loginEmail: string;
  loginSending: boolean;
  loginSent: LoginResponse | null;
  loginError: string | null;

  /**
   * Domain verification for the draft's tenant. `claim` is what the server
   * has on record; `challenge` is the one this page issued (its token is in
   * this response and nowhere else); `verification` is the last answer to
   * "verify now", attempts and all.
   */
  claim: OwnershipStatus | null;
  challenge: OwnershipChallenge | null;
  challengeBusy: boolean;
  challengeError: string | null;
  verification: OwnershipVerification | null;
  verifying: boolean;

  /** Publish */
  /** The operator's own publish secret. Kept in memory for this page only. */
  publishSecret: string;
  publishing: boolean;
  published: { webUrl: string; mcpUrl: string } | null;
  publishError: PublishFailure | null;

  /** Which copy button last succeeded, so it can say so for a moment. */
  copied: string | null;
}

const STAGE_META: ReadonlyArray<{ key: Phase; label: string; gloss: string }> = [
  {
    key: 'intake',
    label: 'Intake',
    gloss: 'Follow redirects, read robots.txt, fingerprint the stack',
  },
  {
    key: 'observe',
    label: 'Observe',
    gloss: 'Fetch pages at 1 rps; collect forms, listings and JSON endpoints',
  },
  {
    key: 'brand',
    label: 'Brand',
    gloss: 'Recover the palette, type scale and radii from the site’s own CSS',
  },
  {
    key: 'synthesize',
    label: 'Synthesize',
    gloss: 'Propose the verbs a customer can perform, as declarative bindings',
  },
  {
    key: 'probe',
    label: 'Probe',
    gloss: 'Execute every proposed binding against the live origin',
  },
  {
    key: 'assemble',
    label: 'Assemble',
    gloss: 'Compile what cleared into one versioned manifest',
  },
];

function freshStages(): StageState[] {
  return STAGE_META.map((m, i) => ({
    key: m.key,
    ordinal: i + 1,
    label: m.label,
    gloss: m.gloss,
    status: 'waiting' as const,
    notes: [],
  }));
}

export function initialState(mode: 'live' | 'demo'): ConsoleState {
  return {
    screen: 'landing',
    mode,
    input: '',
    inputError: null,
    starting: false,
    ownership: 'third-party',
    pendingUrl: null,
    resumeUrl: null,
    jobId: null,
    tenantId: null,
    targetUrl: null,
    startedAt: null,
    finishedAt: null,
    pct: 0,
    stages: freshStages(),
    proposals: [],
    clearances: [],
    fatal: null,
    manifest: null,
    acknowledged: false,
    principal: null,
    principalChecked: false,
    loginEmail: '',
    loginSending: false,
    loginSent: null,
    loginError: null,
    claim: null,
    challenge: null,
    challengeBusy: false,
    challengeError: null,
    verification: null,
    verifying: false,
    publishSecret: '',
    publishing: false,
    published: null,
    publishError: null,
    copied: null,
  };
}

type Listener = () => void;

/**
 * One mutable state object plus a repaint signal — the same shape apps/web uses.
 *
 * A reducer/immutability layer would buy nothing here: there is exactly one
 * writer, and the SSE stream can deliver several frames inside one animation
 * frame, so what actually needs coalescing is the PAINT, not the state.
 */
export class Store {
  state: ConsoleState;
  private listeners = new Set<Listener>();
  private queued = false;

  constructor(mode: 'live' | 'demo') {
    this.state = initialState(mode);
  }

  subscribe(fn: Listener): () => void {
    this.listeners.add(fn);
    return () => {
      this.listeners.delete(fn);
    };
  }

  /** Coalesce bursts of SSE frames into one paint per frame. */
  notify(): void {
    if (this.queued) return;
    this.queued = true;
    requestAnimationFrame(() => {
      this.queued = false;
      for (const fn of this.listeners) fn();
    });
  }

  patch(next: Partial<ConsoleState>): void {
    Object.assign(this.state, next);
    this.notify();
  }

  reset(): void {
    const { mode, principal, principalChecked } = this.state;
    this.state = { ...initialState(mode), principal, principalChecked };
    this.notify();
  }

  beginJob(jobId: string, tenantId: string, url: string): void {
    const { mode, ownership, principal, principalChecked } = this.state;
    this.state = {
      ...initialState(mode),
      screen: 'progress',
      ownership,
      principal,
      principalChecked,
      jobId,
      tenantId,
      targetUrl: url,
      startedAt: Date.now(),
      input: url,
    };
    this.notify();
  }

  private stage(key: Phase): StageState | undefined {
    return this.state.stages.find((s) => s.key === key);
  }

  /**
   * Mark `key` running and everything before it done.
   *
   * Streams drop frames and servers reorder them, so stage entry is expressed
   * as "we are at least here" rather than as a strict transition. A ledger that
   * silently strands stage 3 on `waiting` because one frame was lost is worse
   * than one that infers the obvious.
   */
  private enter(key: Phase): void {
    const target = PHASES.indexOf(key);
    if (target < 0) return;
    const now = Date.now();
    this.state.stages.forEach((s, i) => {
      if (i < target && s.status !== 'failed') {
        if (s.status !== 'done') s.endedAt = now;
        s.status = 'done';
      } else if (i === target && s.status === 'waiting') {
        s.status = 'running';
        s.startedAt = now;
      }
    });
  }

  /**
   * Fold one SSE frame into state.
   *
   * Precedence is deliberate: a structured `capability` field beats a parsed log
   * line, and a parsed `── n/6 stage` header beats the `phase` field — the
   * header is emitted exactly once per stage, while `phase` repeats on every
   * frame and is the field most likely to lag behind what the log is saying.
   */
  apply(e: ProgressEvent): void {
    const message = (e.message ?? '').replace(/\s+$/, '');

    if (typeof e.pct === 'number' && Number.isFinite(e.pct)) {
      this.state.pct = Math.max(this.state.pct, Math.min(100, Math.round(e.pct)));
    }

    const header = message ? parseStageHeader(message) : null;
    if (header) {
      this.enter(header.phase);
      this.notify();
      return;
    }

    const phase = normalizePhase(e.phase);
    if (phase) this.enter(phase);

    if (e.capability) {
      this.record({
        id: e.capability,
        verdict: e.verified === false ? 'turned-back' : 'cleared',
        rows: e.rows ?? 0,
        passRate: e.passRate ?? (e.verified === false ? 0 : 1),
        ...(e.samples !== undefined ? { samples: e.samples } : {}),
        ...(e.error ? { reason: e.error } : {}),
      });
      this.notify();
      return;
    }

    if (message) {
      const verdict = parseProbeLine(message);
      if (verdict) {
        this.enter('probe');
        this.record(verdict);
        this.notify();
        return;
      }

      const active0 = this.activeStage() ?? (phase ? this.stage(phase) : undefined);

      // A declaration, not a log line: it belongs to the tally, not the notes.
      if (active0?.key === 'synthesize') {
        const proposed = parseProposalLine(message);
        if (proposed && !this.state.proposals.includes(proposed)) {
          this.state.proposals.push(proposed);
          this.notify();
          return;
        }
      }

      // The probe prints a running commentary and then a verdict for the same
      // capability. Only the verdict reaches the ledger.
      if (active0?.key === 'probe' && isProbeChatter(message)) {
        this.notify();
        return;
      }

      const active = active0;
      if (active) {
        const note = clip(stripBullet(message), 160);
        const last = active.notes[active.notes.length - 1];
        if (note && note !== last) {
          active.notes.push(note);
          // The ledger shows a stage's most recent lines, not its whole log.
          if (active.notes.length > 6) active.notes.shift();
        }
      }
    }

    if (e.error) this.state.fatal = e.error;
    this.notify();
  }

  private record(v: Omit<Clearance, 'seq'>): void {
    const at = this.state.clearances.findIndex((c) => c.id === v.id);
    const prev = at >= 0 ? this.state.clearances[at] : undefined;
    if (prev) {
      this.state.clearances[at] = { ...prev, ...v };
      return;
    }
    this.state.clearances.push({ ...v, seq: this.state.clearances.length });
  }

  activeStage(): StageState | undefined {
    return this.state.stages.find((s) => s.status === 'running');
  }

  finish(manifest: Manifest): void {
    const now = Date.now();
    for (const s of this.state.stages) {
      if (s.status === 'failed') continue;
      if (s.status !== 'done') s.endedAt = now;
      s.status = 'done';
    }
    this.state.pct = 100;
    this.state.finishedAt = now;
    this.state.manifest = manifest;
    this.state.screen = 'result';
    this.notify();
  }

  fail(message: string): void {
    const active = this.activeStage();
    if (active) {
      active.status = 'failed';
      active.endedAt = Date.now();
    }
    this.state.fatal = message;
    this.state.finishedAt = Date.now();
    this.state.starting = false;
    this.notify();
  }
}

/**
 * The server picks its own phase spelling. Rather than demand it match, map the
 * spellings a reasonable implementer would land on. An unknown phase returns
 * null and the frame is treated as a detail line, which is harmless.
 */
function normalizePhase(input: string | undefined): Phase | null {
  if (!input) return null;
  const v = input.trim().toLowerCase();
  const direct = PHASES.find((p) => p === v);
  if (direct) return direct;
  if (v.startsWith('intake') || v.startsWith('resolv')) return 'intake';
  if (v.startsWith('crawl') || v.startsWith('fetch') || v.startsWith('observ')) return 'observe';
  if (v.startsWith('brand') || v.startsWith('token') || v.startsWith('extract')) return 'brand';
  if (v.startsWith('synth') || v.startsWith('propos')) return 'synthesize';
  if (v.startsWith('probe') || v.startsWith('verif')) return 'probe';
  if (v.startsWith('assembl') || v.startsWith('compil') || v.startsWith('manifest')) {
    return 'assemble';
  }
  return null;
}

/** Elapsed as `0:14` or `1:02:30`, for the running clock. */
export function elapsed(from: number, to: number): string {
  const total = Math.max(0, Math.round((to - from) / 1000));
  const pad = (n: number): string => String(n).padStart(2, '0');
  const s = total % 60;
  const m = Math.floor(total / 60) % 60;
  const h = Math.floor(total / 3600);
  return h > 0 ? `${h}:${pad(m)}:${pad(s)}` : `${m}:${pad(s)}`;
}
